<template>
    <v-row>
        <v-col cols="12">
            <div class="d-flex align-center justify-space-between">
                <h2 class="text--primary text-lg font-weight-medium">
                    {{ $t("Palautekanava") }}
                </h2>
                <div>
                    <v-btn text @click="$router.push({ name: 'admin-feedback-list' })">
                        {{ $t('Vanha näkymä') }}
                    </v-btn>
                    <v-btn color="primary" :loading="isLoading" @click="refresh">
                        {{ $t('Päivitä') }}
                    </v-btn>
                </div>
            </div>
        </v-col>
        <v-col cols="12" class="d-flex align-center justify-space-between">
            <v-switch v-model="showCompleted" dense inset hide-details class="mr-3" :label="$t('Näytä valmiit')" />

            <v-menu offset-y>
                <template v-slot:activator="{ attrs, on }">
                    <v-btn text small color="primary" v-bind="attrs" v-on="on" :disabled="!exportMenuItems.length"
                        :loading="orange">
                        <v-icon v-if="!isExporting" left small>{{ icons.mdiDownload }}</v-icon>
                        <v-progress-circular v-else class="mr-2" size="14" :width="1"
                            indeterminate></v-progress-circular>

                        {{ $t('Vie palautteet') }}
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item v-for="cat in exportMenuItems" :key="'export-' + cat.category_id"
                        @click="exportCategory(cat)">
                        <v-list-item-title>{{ cat.category }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-col>

        <v-col cols="12" md="7">
            <v-card outlined>
                <v-card-title class="d-flex justify-space-between"
                    :style="{ 'border-left': '4px solid ' + (showCompleted ? 'green' : 'red') }">
                    <span>
                        {{ showCompleted ? $t('Valmiit') : $t('Saapuneet') }}
                    </span>

                    <div class="d-flex align-center">

                        <v-chip small :color="showCompleted ? 'success' : 'error'" text-color="white">
                            {{ showCompleted ? completedCount : newCount }}
                        </v-chip>
                    </div>
                </v-card-title>
                <v-divider></v-divider>

                <feedback-dashboard-list :items="showCompleted ? completedItems : newItems"
                    :is-loading="showCompleted ? isCompletedLoading : isLoading" :status="showCompleted ? 3 : 1"
                    :empty-text="showCompleted ? $t('Ei valmiita palautteita') : $t('Ei saapuneita palautteita')"
                    @select="openFeedback" />

                <v-divider></v-divider>
                <v-card-actions class="justify-space-between">
                    <v-btn text :disabled="showCompleted ? completedPage <= 1 : newPage <= 1"
                        @click="showCompleted ? completedPage-- : newPage--">
                        {{ $t('Edellinen') }}
                    </v-btn>
                    <span class="text-caption">
                        {{ $t('Sivu') }} {{ showCompleted ? completedPage : newPage }}
                    </span>
                    <v-btn text :disabled="showCompleted ? !completedNext : !newNext"
                        @click="showCompleted ? completedPage++ : newPage++">
                        {{ $t('Seuraava') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-col>

        <v-col cols="12" md="5">
            <v-card outlined>
                <v-card-title class="d-flex justify-space-between" style="border-left: 4px solid orange;">
                    <span>{{ $t('Käsittely kesken') }}</span>
                    <v-chip small color="warning" text-color="white">{{ openCount }}</v-chip>
                </v-card-title>
                <v-divider></v-divider>

                <feedback-dashboard-list :items="openItems" :is-loading="isLoading" :status="2"
                    :empty-text="$t('Ei käsittelyssä olevia palautteita')" @select="openFeedback" />

                <v-divider></v-divider>
                <v-card-actions class="justify-space-between">
                    <v-btn text :disabled="openPage <= 1" @click="openPage--">
                        {{ $t('Edellinen') }}
                    </v-btn>
                    <span class="text-caption">{{ $t('Sivu') }} {{ openPage }}</span>
                    <v-btn text :disabled="!openNext" @click="openPage++">
                        {{ $t('Seuraava') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-col>

        <v-col cols="12">
            <feedback-dashboard-reports @categories-loaded="onCategoriesLoaded" />
        </v-col>

    </v-row>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { mdiDownload } from "@mdi/js";
import { i18n } from "@/plugins/i18n";
import FeedbackDashboardList from "@/views/admin/feedback/components/FeedbackDashboardList.vue";
import FeedbackDashboardReports from "@/views/admin/feedback/components/FeedbackDashboardReports.vue";

export default {
    name: "FeedbackDashboard",
    components: {
        FeedbackDashboardList,
        FeedbackDashboardReports,
    },
    setup(_, { root }) {
        const isLoading = ref(false);
        const isCompletedLoading = ref(false);

        const pageSize = 10;

        const newPage = ref(1);
        const newItems = ref([]);
        const newCount = ref(0);
        const newNext = ref(null);

        const openPage = ref(1);
        const openItems = ref([]);
        const openCount = ref(0);
        const openNext = ref(null);

        const showCompleted = ref(false);
        const completedPage = ref(1);
        const completedItems = ref([]);
        const completedCount = ref(0);
        const completedNext = ref(null);

        const availableCategories = ref([]);
        const isExporting = ref(false);

        const exportMenuItems = computed(() => [
            ...availableCategories.value,
            { category_id: null, category: i18n.t('Ei luokiteltu') },
        ]);

        const fetchNew = async () => {
            isLoading.value = true;
            const res = await adminApi.getFeedbacksV2({ status: 1, page: newPage.value, pageSize });
            if (res.ok) {
                newItems.value = res.data?.results || [];
                newCount.value = res.data?.count ?? 0;
                newNext.value = res.data?.next || null;
            } else {
                alert(res.error);
            }
            isLoading.value = false;
        };

        const fetchOpen = async () => {
            isLoading.value = true;
            const res = await adminApi.getFeedbacksV2({ status: 2, page: openPage.value, pageSize });
            if (res.ok) {
                openItems.value = res.data?.results || [];
                openCount.value = res.data?.count ?? 0;
                openNext.value = res.data?.next || null;
            } else {
                alert(res.error);
            }
            isLoading.value = false;
        };

        const fetchCompleted = async () => {
            isCompletedLoading.value = true;
            const res = await adminApi.getFeedbacksV2({ status: 3, page: completedPage.value, pageSize });
            if (res.ok) {
                completedItems.value = res.data?.results || [];
                completedCount.value = res.data?.count ?? 0;
                completedNext.value = res.data?.next || null;
            } else {
                alert(res.error);
            }
            isCompletedLoading.value = false;
        };

        const refresh = async () => {
            if (showCompleted.value) {
                await fetchCompleted();
            } else {
                await fetchNew();
            }
            await fetchOpen();
        };

        const openFeedback = (fb) => {
            root.$router.push({ name: "admin-feedback-handle", params: { id: String(fb.id) } });
        };

        const onCategoriesLoaded = (categories) => {

            availableCategories.value = categories;
        };

        const exportCategory = async (category) => {
            if (isExporting.value) return;
            isExporting.value = true;

            try {
                const response = await adminApi.exportFeedbackCategorySubmissions(category.category_id);

                if (!response.ok) {
                    alert(i18n.t('Vastausten vienti epäonnistui'));
                    return;
                }

                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `${category.category}_vastaukset_${Date.now()}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            } finally {
                isExporting.value = false;
            }
        };

        onMounted(() => {
            refresh();
        });

        watch(newPage, () => {
            if (!showCompleted.value) {
                fetchNew();
            }
        });
        watch(openPage, () => fetchOpen());
        watch(completedPage, () => {
            if (showCompleted.value) {
                fetchCompleted();
            }
        });

        watch(showCompleted, (val) => {
            if (val) {
                completedPage.value = 1;
                fetchCompleted();
            } else {
                newPage.value = 1;
                fetchNew();
            }
        });

        return {
            isLoading,
            isCompletedLoading,
            refresh,
            newItems,
            newCount,
            newNext,
            newPage,
            openItems,
            openCount,
            openNext,
            openPage,
            showCompleted,
            completedItems,
            completedCount,
            completedNext,
            completedPage,
            openFeedback,
            availableCategories,
            exportMenuItems,
            isExporting,
            onCategoriesLoaded,
            exportCategory,
            icons: {
                mdiDownload,
            },
        };
    },
};
</script>

<style scoped></style>
